import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import {
  Eye,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
} from "lucide-react";
import { memo } from "react";
import { formatCPF } from "@/shared/lib/formatters";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import type { SellerResponseDTO } from "../types/seller.types";

interface SellersTableProps {
  sellers: SellerResponseDTO[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onEdit: (seller: SellerResponseDTO) => void;
  onDelete: (seller: SellerResponseDTO) => void;
  onView: (seller: SellerResponseDTO) => void;
  onPageChange: (page: number) => void;
}

const formatDocument = (document: string) => {
  const raw = document.replace(/\D/g, "");
  return raw.length === 11 ? formatCPF(raw) : document;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

export const SellersTable = memo(function SellersTable({
  sellers,
  loading,
  currentPage,
  totalPages,
  onEdit,
  onDelete,
  onView,
  onPageChange,
}: SellersTableProps) {
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Documento</TableHead>
            <TableHead>Aluguéis</TableHead>
            <TableHead>Receita Total</TableHead>
            <TableHead>Comissão</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell
                colSpan={6}
                className="text-center py-10 text-muted-foreground"
              >
                Carregando...
              </TableCell>
            </TableRow>
          ) : sellers.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={6}
                className="text-center py-10 text-muted-foreground"
              >
                Nenhum vendedor encontrado
              </TableCell>
            </TableRow>
          ) : (
            sellers.map((seller) => (
              <TableRow key={seller.id}>
                <TableCell className="font-medium text-foreground">
                  {seller.name}
                </TableCell>
                <TableCell>{formatDocument(seller.document)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
                    <span>{seller.monthlyRentalCount ?? 0}</span>
                  </div>
                </TableCell>
                <TableCell className="text-blue-400 font-medium">
                  {formatCurrency(seller.monthlyRentalRevenue ?? 0)}
                </TableCell>
                <TableCell className="text-green-400 font-medium">
                  {formatCurrency(seller.monthlyCommissionTotal ?? 0)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onView(seller)}
                      title="Ver aluguéis"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Can permission={ACTIONS.sellers.update}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(seller)}
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Can>
                    <Can permission={ACTIONS.sellers.delete}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(seller)}
                        title="Excluir"
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </Can>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
